// For valor and terminal (always have match status from backend)
export type OCRFieldResult = {
  encontrado: string | number | null; // required (can be null)
  esperado?: string | number; // optional
  match: boolean; // required — prevents silent "success" when backend omits the field
};

// For data (detection only, no match concept)
export type OCRDataField = {
  encontrado?: string | null;
};

export type OCRResultLike = {
  sucesso: boolean;
  valor: OCRFieldResult;
  terminal: OCRFieldResult;
  data?: OCRDataField;
};

export type BadgeState = "success" | "warning" | "error";

const TIMING = {
  VALOR_MS: 600,
  TERMINAL_MS: 1200,
  DATA_MS: 1800,
  BADGE_FULL_MS: 2200,
  BADGE_NO_DATA_MS: 1400,
} as const;

function computeBadge(result: OCRResultLike): BadgeState {
  if (!result.sucesso) return "error";
  const fields: Array<OCRFieldResult> = [result.valor, result.terminal];
  const anyMismatch = fields.some((f) => f.match === false);
  return anyMismatch ? "warning" : "success";
}

/**
 * Schedules progressive reveal of OCR result fields.
 * Returns a cleanup function that cancels pending timeouts (call on unmount).
 */
export function progressiveReveal(
  result: OCRResultLike,
  onReveal: (
    field: "valor" | "terminal" | "data",
    value: OCRFieldResult | OCRDataField
  ) => void,
  onComplete: (badge: BadgeState) => void
): () => void {
  const timeouts: ReturnType<typeof setTimeout>[] = [];
  const hasData = !!result.data;

  timeouts.push(setTimeout(() => onReveal("valor", result.valor), TIMING.VALOR_MS));
  timeouts.push(setTimeout(() => onReveal("terminal", result.terminal), TIMING.TERMINAL_MS));

  if (hasData) {
    timeouts.push(setTimeout(() => onReveal("data", result.data!), TIMING.DATA_MS));
  }

  const completeMs = hasData ? TIMING.BADGE_FULL_MS : TIMING.BADGE_NO_DATA_MS;
  timeouts.push(setTimeout(() => onComplete(computeBadge(result)), completeMs));

  return () => timeouts.forEach(clearTimeout);
}
